'use client';

import React, { type HTMLAttributes } from 'react';
import { css } from 'styled-components';

import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import TrustYouReviewSection from '@/app/components/hotel-detail/trust-you/TrustYouReviewsSection';
import TripAdvisorReviewsSection from '@/app/components/hotel-detail/trip-advisor/TripAdvisorReviewsSection';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { hasReviewsContent, isTripAdvisorSource } from '@/app/common/reviews/reviewSource';
import customCss from './css/customCss';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { useIsMobile } from './hooks/useMediaSSR';
import { useTranslation } from '../translations/Translation';

type HotelPageReviewSectionProps = {
  language: string;
  /** Render TripAdvisor reviews inline (full list) instead of a teaser + "view more" modal — used in the Evo details modal. */
  inlineReviews?: boolean;
} & HTMLAttributes<HTMLElement>;

const REVIEWS_SECTION_TITLE_ID = 'reviews-section-title';

export default function HotelPageReviewSection(props: HotelPageReviewSectionProps) {
  const { language, inlineReviews, ...rest } = props;
  const { t } = useTranslation();
  const { data: staticData } = useFetchHotelStatic();
  const isMobile = useIsMobile();
  const { localization } = usePageWrapperData();
  const { reviewsDisabled } = localization.configValuesApp;

  const isTripAdvisor = isTripAdvisorSource(staticData?.hotelOpinion.reviewSource);
  // TA needs a rating, TY needs its genuine opinionId — any other source shows no reviews at all.
  const hasReviewData = hasReviewsContent(staticData?.hotelOpinion);

  return (
    <ErrorBoundary id="HotelPageReviewSection">
      <div {...rest}>
        {!reviewsDisabled && hasReviewData ? (
          <HotelPageContentSection
            // Inline TA reviews render their own "Guest reviews" header (aligned with the TA attribution).
            title={
              isTripAdvisor && inlineReviews
                ? undefined
                : isMobile === true || isMobile === undefined
                  ? t('vacation.select.details.guest.ratings.title')
                  : t('vacation.select.details.guest.ratings_reviews.title')
            }
            titleId={REVIEWS_SECTION_TITLE_ID}
            css={css`
              display: flex;
              flex-direction: column;
              background-color: white;

              @media ${customCss.breakpoints.sm} {
                background-color: inherit;
              }
            `}
          >
            {isTripAdvisor ? (
              <TripAdvisorReviewsSection
                hotelName={staticData!.name}
                hotelOpinion={staticData!.hotelOpinion}
                showMoreInfoEnabled={staticData!.isTopSecret === false}
                reviewsSectionTitleId={REVIEWS_SECTION_TITLE_ID}
                inline={inlineReviews}
                {...rest}
              />
            ) : (
              <TrustYouReviewSection
                hotelName={staticData!.name}
                language={language}
                hotelOpinion={staticData!.hotelOpinion}
                showMoreInfoEnabled={staticData!.isTopSecret === false}
                reviewsSectionTitleId={REVIEWS_SECTION_TITLE_ID}
                inline={inlineReviews}
                {...rest}
              />
            )}
          </HotelPageContentSection>
        ) : undefined}
      </div>
    </ErrorBoundary>
  );
}
